/**
 * `/lp/:audience` — one clean URL per campaign, serving the v3 landing page.
 *
 * Separate from `/c/:audience` on purpose. That path already serves the older
 * campaign experience for `journalist` and `marketing`, which are live ad
 * destinations, so the new design could only reach them behind `?v=v3`. A
 * query parameter is a poor thing to put in an ad: it is easy to drop when a
 * URL is copied, some networks rewrite or strip parameters, and it collides
 * with the `utm_*` set the same URL has to carry.
 *
 * So each campaign gets its own address instead. `/lp/newsroom` exists too,
 * rather than leaving newsroom alone on `/c/newsroom`, so all three ad URLs
 * have the same shape and nobody has to remember which one is different.
 * `/c/newsroom` keeps working.
 *
 * This route ALWAYS renders v3, with no variant lookup: the whole point is a
 * URL whose output does not depend on a query parameter or on what the
 * visitor happens to have in localStorage from an earlier visit.
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import LandingPageV3 from './LandingPageV3';
import { landingConfigFor } from './landingConfig';

export default function LandingRoute() {
  const { audience } = useParams();
  const navigate = useNavigate();
  const config = landingConfigFor(audience);

  // An unknown slug is not a campaign destination, and the visitor is a
  // stranger who mistyped an ad URL or followed a mangled one. Three options
  // and only one is right:
  //
  //   - Render nothing, as an unknown `/c/` segment does. A blank page.
  //   - Redirect to `/`, which is an AUTHENTICATED route, so it bounces
  //     straight into the Frontegg login screen. Verified; it is the worst of
  //     the three and was this component's first behaviour.
  //   - Send them to the public marketing site, which is the nearest thing to
  //     what they were looking for.
  //
  // Coercing the slug to a default campaign is deliberately not on the list:
  // §1 of the campaign PRD forbids it, and it would record the wrong campaign
  // against every event the visitor then generates.
  useEffect(() => {
    if (!config) window.location.replace('https://alchemiq.ai/');
  }, [config]);

  if (!config) return null;

  // A trial signup leaves this page for the trial flow's own confirmation
  // screen, which the design makes page 1 of that flow. The callback lives here
  // rather than in `LandingPageV3` so that component stays router-free and
  // renders in a test without a `MemoryRouter`.
  return (
    <LandingPageV3
      config={config}
      onTrialSubmitted={() => navigate('/trial/confirmation')}
    />
  );
}
